import {
  get,
  set,
  isNil,
  orderBy,
  assign,
  entries,
  keys,
} from "@/utils/object";
import type {
  ControlNodeType,
  ControlSchema,
} from "@binaryoperations/json-schema-form/core/models";
import { evalVisibleConditions } from "@/store/statePresenter";
import type { ConfirmationObjectSchema as ObjectJsonSchema } from "@/components/SchemaForm/schemaDraft";
import type { FormState } from "../../FormManager/FormState";

import { FormFieldModelCollection } from "./FormFieldModelCollection";
import { BookModel } from "../BookModel/BookModel";
import type { BookModelCollection } from "../BookModel/BookModelCollection";
import { isBooleanTrue } from "@/utils/string";
import { apiDateOrNull } from "@/utils/datetime";
import { translate } from "@/utils/translationService";
import type { JsonSchema } from "@binaryoperations/json-schema-form/core/lib";

export type FieldControl = ControlNodeType<
  object,
  {
    options?: { label: string; value: string | number | boolean }[];
    format: string;
    label?: string;
    name: string;
    disabled?: boolean;
    description?: string;
    required?: boolean;
    canPaste?: boolean;
    autoComplete?: AutoFill;
  }
>;

export type FormObject = FormState | Record<string, unknown> | null | undefined;

export type PrepareFieldConfig = {
  validationErrors?: Record<string, string[]> | null;
  data?: object;
  index?: number;
  validationPrefix?: string;
  prefix?: string;
};

export type VisibleConditions = Record<string, string>;

export class FormFieldModel extends BookModel {
  // is_required: boolean;
  // name: string;
  // title: string;
  // visible_conditions: Record<string, any> | null;
  declare switchable_show_on_yes: 1 | 0;
  declare switchable_title: string | null;
  declare switchable_description: string | null;
  declare confirmation?: boolean | null;

  declare title: string;
  declare description: string;

  get isConsent() {
    return false;
  }

  static getCollectionClass() {
    return FormFieldModelCollection;
  }

  private fieldTypeMap: Record<string, string> = {
    image: "file",
    email: "text",
    salutation: "select",
    country: "select",
    yesnoradio: "radio-group",
    date: "date-picker",
    birthday: "date",
  };

  get originalFieldType() {
    return this.hasOne("formFieldType")!.name;
  }

  get fieldType() {
    const type = this.originalFieldType;
    return this.fieldTypeMap[type] || type;
  }

  get visibleConditions() {
    return this.get<VisibleConditions>("visible_conditions") ?? undefined;
  }

  get defaultValue() {
    const defaultValue = this.get("presented.defaultValue");

    if (defaultValue === null) return;

    return this.normalizeValue(this.originalFieldType, defaultValue);
  }

  get switchableFieldName() {
    return `$$switchable_${this.getItemName()}`;
  }

  get confirmationFieldName() {
    return `$$confirm_${this.getItemName()}`;
  }

  toBillRecipientField() {
    const recipient = this.get<{ title: string; is_required: boolean; confirmationTitle?: string }>("presented.billRecipient");
    const field = this.clone();
    if (!recipient) return field;

    return assign(field, recipient, {
      presented: {
        ...this.presented,
        title: recipient.title,
        confirmationTitle: recipient.confirmationTitle,
      },
    });
  }

  getFieldOptions(fieldtype = this.originalFieldType) {
    switch (fieldtype) {
      case "salutation":
        return this.modelManager
          .getCollection<BookModelCollection>("salutations")
          .getAsSelectOptions({ label: "humanIdentifier" });

      case "country":
        return this.modelManager
          .getCollection<BookModelCollection>("countries")
          .getAsSelectOptions();

      case "select":
        return this.hasMany<BookModelCollection>("selectOptions")
          .all()
          .map((option) => ({
            label: option.get(
              "presented.label",
              option.get<string>("name", ""),
            ),
            value: String(option.id),
          }));
      case "yesnoradio":
        return [
          {
            label: this.options.translate("switchable.yes"),
            value: "true",
          },
          {
            label: this.options.translate("switchable.no"),
            value: "false",
          },
        ];
      default:
    }
  }

  getItemName(): string {
    return (this.get("presented.name") || this.get("name")) as string;
  }

  isRequired(
    form?: FormState | Record<string, unknown> | null | undefined,
    fieldName?: string,
  ) {
    if (form?.isValidationDisabled) return false;

    if (this.get("is_required")) {
      return true;
    }

    if (this.originalFieldType === "yesnoradio") {
      return true;
    }

    if (this.switchable_show_on_yes) return true;

    if (!fieldName) return false;

    return this.isConditionallyRequired(form, fieldName);
  }

  isConditionallyRequired(
    form: FormState | Record<string, unknown> | null | undefined,
    fieldName: string,
  ) {
    const validationRule = this.get("validation_rule") as string;
    if (!validationRule?.trim()) {
      return false;
    }

    const value = get(form, fieldName);

    if (value) return true;

    const nameArr = fieldName.replace(/\//g, ".").split(".");
    const rules = validationRule
      .split("|")
      .map((rule) => rule.split(":"))
      .reduce(
        (all, [rule = "", values = ""]) => {
          rule = rule?.trim();
          const dependencies = values
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean);

          switch (rule) {
            case "required_without": {
              all[rule] = dependencies.map((fieldName) => {
                nameArr[nameArr.length - 1] = fieldName;
                return nameArr.join(".");
              });
              return all;
            }
            case "required_if_in_array": {
              const [fieldName, isRequired] = dependencies;
              nameArr[nameArr.length - 1] = fieldName;

              all[rule] = {
                ...all[rule],
                [fieldName]: isBooleanTrue(isRequired),
              };
              return all;
            }
            case "required_without_in_array": {
              all[rule] = values
                .split(",")
                .map((x) => x.trim())
                .map((name) => {
                  nameArr[nameArr.length - 1] = name;
                  return nameArr.join(".");
                });

              return all;
            }
            default:
              return all;
          }
        },
        {} as {
          required_without?: string[];
          required_without_in_array?: string[];
          required_if_in_array?: Record<string, boolean>;
        },
      );

    return keys(rules).reduce((isRequired, rule) => {
      switch (rule) {
        case "required_without":
          return (
            isRequired ||
            rules.required_without!.some((name) => !get(form, name))
          );
        case "required_without_in_array":
          return (
            isRequired ||
            rules.required_without_in_array!.some((name) => !get(form, name))
          );

        case "required_if_in_array": {
          return (
            isRequired ||
            entries(rules.required_if_in_array!).some(
              ([name, isRequired]) => !!get(form, name) && isRequired,
            )
          );
        }
      }

      return false;
    }, false);
  }

  private deriveSchemaType(
    form: FormObject,
    config: PrepareFieldConfig = {},
    name = this.getItemName(),
    fieldType = this.originalFieldType,
    defaultValue = this.defaultValue,
    moreOptions = {},
  ): ControlSchema {
    const { index, prefix } = config;
    const fieldPath = [prefix, index, name]
      .filter((part) => !isNil(part) && part !== "")
      .join(".")
      .replaceAll("/", ".");

    const options = {
      options: {
        ...moreOptions,
      },
    };

    const nullableType = this.isRequired(form, fieldPath)
      ? []
      : (["null"] as const);

    let format: undefined | string;

    switch (fieldType) {
      case "yesnoradio": {
        return {
          type: ["string"],
          ...options,
        };
      }
      case "select":
      case "salutation":
      case "country":
        return {
          type: ["string", ...nullableType],
          default: String(defaultValue || ""),
          ...options,
        };
      case "textarea":
        return {
          type: ["string", ...nullableType],
          default: String(defaultValue || ""),
          ...options,
        };
      case "switch":
      case "checkbox":
        return {
          type: ["boolean", ...nullableType],
          default: false,
          ...options,
        };
      case "birthday":
      case "date":
        return {
          type: ["string", ...nullableType],
          default: String(defaultValue || ""),
          ...options,
        };
      case "file":
      case "image":
        return {
          type: ["array", ...nullableType],
          default: [],
          ...(this.isRequired(form, fieldPath)
            ? {
                minItems: 1,
                errorMessages: {
                  "min-items-error": translate("errors.required_with_label", {
                    label: this.getFieldMeta().label,
                  }),
                },
              }
            : {}),
          items: {
            type: "object",
          },
          ...options,
        };

      case "email":
      case "text":
        return {
          type: ["string", ...nullableType],
          default: String(this.defaultValue || ""),
          format,
          ...options,
        };
      case "number":
        return {
          default: this.defaultValue ?? null,
          type: ["number", ...nullableType],
          ...options,
        };
    }

    return {
      type: "null",
      options: {
        message: translate("errors.field_type_not_available", {
          type: this.fieldType,
        }),
      },
    };
  }

  private showsSwitchableField(
    formData: FormObject,
    prefix: string,
    config?: PrepareFieldConfig,
  ) {
    if (!this.switchable_show_on_yes) return true;
    return isBooleanTrue(
      get(
        formData,
        this.derivePathName(prefix, config, this.switchableFieldName).name,
      ),
    );
  }

  private buildNestedRelatedFieldSchema(
    form: FormObject,
    config: PrepareFieldConfig = {},
  ): ObjectJsonSchema {
    const properties: JsonSchema = {};
    const required: string[] = [];
    const confirmFields: Record<string, string> = {};

    if (this.switchable_show_on_yes) {
      required.push(this.switchableFieldName);
      properties[this.switchableFieldName] = this.deriveSchemaType(
        form,
        config,
        this.switchableFieldName,
        "yesnoradio",
        null,
      );
    }

    if (this.confirmation) {
      switch (this.fieldType) {
        case "text": {
          properties[this.confirmationFieldName] = {
            ...this.deriveSchemaType(
              form,
              config,
              this.confirmationFieldName,
              this.originalFieldType,
              null,
              {
                referenceFieldLabel: this.get(
                  "presented.title",
                  this.get("title", ""),
                ),
              },
            ),
          };

          confirmFields[this.confirmationFieldName] = this.getItemName();
        }
      }
    }

    return {
      type: "object",
      properties,
      required,
      ...(keys(confirmFields).length ? { confirmFields } : {}),
    };
  }

  buildFieldSchema(
    form: FormObject,
    config?: PrepareFieldConfig,
  ): ObjectJsonSchema {
    const name = this.getItemName();
    const { required = [], properties, confirmFields } = this.buildNestedRelatedFieldSchema(
      form,
      config,
    );

    const requiredFieldName = this.switchable_show_on_yes
      ? this.switchableFieldName
      : this.isRequired(
            form,
            [config?.prefix, config?.index, name]
              .filter((x) => !isNil(x))
              .join(".")
              .replaceAll("/", "."),
          )
        ? name
        : null;

    return {
      type: "object",
      properties: {
        [name]: this.deriveSchemaType(form, config),
        ...properties,
      },
      required: [
        requiredFieldName,
        ...required,
        ...(requiredFieldName === name ? keys(confirmFields ?? {}) : []),
      ].filter((x) => x !== null),
      ...(confirmFields ? { confirmFields } : {}),
    };
  }

  deriveValidationError(errorMessages?: string[] | null) {
    if (!errorMessages?.length) return;
    return {
      errorMessage: errorMessages.at(0),
    };
  }

  private buildNestedFieldControl(
    prefix: string,
    config?: PrepareFieldConfig,
  ): FieldControl[] {
    const controls: FieldControl[] = [];

    if (this.switchable_show_on_yes) {
      const { path, name } = this.derivePathName(
        prefix,
        config,
        this.switchableFieldName,
      );

      controls.push({
        type: "control",
        path,
        order: -1,
        options: {
          name,
          options: this.getFieldOptions("yesnoradio"),
          format: "radio-group",
          label: this.get(
            "presented.switchableTitle",
            this.get("switchable_title", ""),
          ),
          description: this.get(
            "presented.switchableBody",
            this.get("switchable_description", ""),
          ),
          disabled: this.get("is_disabled", false),
          required: true,
          ...this.deriveValidationError(),
        },
      });
    }

    if (this.confirmation) {
      switch (this.fieldType) {
        case "text": {
          const { path, name } = this.derivePathName(
            prefix,
            config,
            this.confirmationFieldName,
          );

          controls.push({
            type: "control",
            path,
            order: 2,
            options: {
              name,
              label: this.get("presented.confirmationTitle", ""),
              format: this.fieldType,
              canPaste: false,
              required: this.isRequired(config?.data as FormObject,
                this.derivePathName(prefix, config).name) ||
                !!get(config?.data, this.derivePathName(prefix, config).name),
              autoComplete: "off",
            },
          });
        }
      }
    }

    return controls;
  }

  buildFieldControl(
    prefix: string,
    formData: FormObject,
    sources: string[] | undefined,
    config?: PrepareFieldConfig,
  ): FieldControl[] {
    const { name, validationFieldName, path, parent } = this.derivePathName(
      prefix,
      config,
    );

    if (
      !evalVisibleConditions(
        get(formData, parent?.replaceAll("/", ".")) as Record<string, unknown>,
        this.visibleConditions,
        sources,
      )
    ) {
      return [];
    }

    const isRequiredBecauseSwitchable = this.showsSwitchableField(
      formData,
      prefix,
      config,
    );

    const initialField: FieldControl[] = !isRequiredBecauseSwitchable
      ? []
      : [
          {
            type: "control",
            path,
            order: 1,
            options: {
              name,
              options: this.getFieldOptions(),
              format: this.fieldType,
              ...this.getFieldMeta(),
              required: this.isRequired(formData, path),
              disabled: this.get("is_disabled", false),
              ...this.deriveValidationError(
                config?.validationErrors?.[validationFieldName],
              ),
            },
          },
        ];

    return orderBy(
      [
        ...initialField,
        ...this.buildNestedFieldControl(prefix, {
          ...config,
          data: formData as object,
        }),
      ],
      ["order"],
      ["asc"],
    ).map((control) => {
      if (!control.options) return { ...control, order: undefined };

      return {
        ...control,
        order: undefined,
        options: {
          ...control.options,
          // Editing restrictions belong to this person's control, not the
          // shared array-item schema used for every person in the booking.
          disabled:
            control.options.disabled ||
            (formData as FormState)?.isParticipantFieldDisabled?.(control.path),
        },
      };
    });
  }

  getFieldMeta() {
    return {
      label: this.get("presented.title", this.get("title", "")),
      description: this.get("presented.body", this.get("description", "")),
    };
  }

  derivePathName = (
    prefix: string,
    config?: PrepareFieldConfig,
    name = this.getItemName(),
  ) => {
    const parentPath = [prefix?.replaceAll(".", "/"), config?.index]
      .filter((part) => !isNil(part) && part !== "")
      .join("/");

    const path = [parentPath, name]
      .filter((part) => !isNil(part) && part !== "")
      .join("/");

    const normalizedName = path.replaceAll("/", ".");

    const validationFieldName = config?.validationErrors
      ? [(config?.validationPrefix ?? prefix)?.replaceAll("/", "."), name]
          .filter((part) => !isNil(part) && part !== "")
          .join(".")
      : "";

    return {
      path,
      parent: parentPath,
      name: normalizedName,
      validationFieldName,
    };
  };

  normalizeFieldValue(
    form: object,
    prefix?: string,
    index?: number,
    itemName = this.getItemName(),
  ) {
    const fieldName = [prefix, index, itemName]
      .filter((part) => !isNil(part))
      .join(".");

    const fieldValue = get(form, fieldName);
    const object = {};

    return set(
      object,
      fieldName,
      this.normalizeValue(this.originalFieldType, fieldValue),
    );
  }

  normalizeValue(type: string, value: unknown, isOutput = true) {
    switch (type) {
      case "checkbox":
      case "yesnoradio": {
        if (isNil(value)) value = undefined;
        else value = typeof value === "boolean" ? value : isBooleanTrue(value);
        break;
      }
      case "number": {
        value = Number(value);
        if (isNaN(+(value as number))) value = undefined;
        break;
      }
      case "date":
      case "birthday": {
        if (!isOutput) {
          if (value && typeof value === "object" && "date" in value) {
            value = value.date;
          }

          return value || undefined;
        }
        // Controls normalize complete dates. Preserve unfinished text so the
        // server can reject it instead of throwing here or saving an old date.
        value = typeof value === "string"
          ? value || undefined
          : apiDateOrNull(value as Date, "date") ?? undefined;
        break;
      }
      case "email":
      case "text":
      case "textarea":
        value = typeof value === "string" ? value : "";
        break;

      case "file":
      case "image":
        return ([] as unknown[]).concat(value).filter(Boolean).length
          ? value
          : undefined;
    }

    return value;
  }
}
